import React, { useEffect, useMemo, useState, useRef } from 'react'

import styled from 'styled-components'

import TrendOverviewCard from 'pages/shared/MaintenanceTrend/TrendOverviewCard'
import PlotTrend from 'pages/shared/MaintenanceTrend/PlotTrend'
import AlertsTooltip from 'pages/shared/AlertsTooltip'

import GA4 from 'helpers/ga4'
import Apptentive from 'helpers/apptentive'

import { DeviceMaintenanceTrendGroup, DeviceMetric, DeviceMaintenanceTrend, Trend } from 'types/DeviceMetric'

import { color } from 'styles/colors'
import { Alert } from 'types/Alert'

type props = {
  trend: DeviceMaintenanceTrend | Trend
  metricAndGroupList: Array<DeviceMaintenanceTrendGroup | DeviceMetric>
  timeZone: string
  showMetricSelection: boolean
  deviceId?: string
  sendAnalyticsEvents: boolean
  alerts: Array<Alert>
  tier?: string | null
}

const MaintenanceTrend = ({ timeZone, metricAndGroupList, alerts, trend, showMetricSelection, deviceId = '', sendAnalyticsEvents = false, tier }: props) => {
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([])
  const trendName = trend.name
  const tooltipId = `${trendName.toLowerCase().replace(/ /g, '-')}-alert-tooltip`
  const hideChart = trend.hideChart
  const showAlert = trend.showAlert || alerts.length > 0
  const hideRanges = trend.hideRanges
  const containerRef = useRef<HTMLDivElement>(null)

  const handleMetricSelection = (metricName: string) => {
    const isMetricSelected = selectedMetrics.includes(metricName)

    if (isMetricSelected) {
      setSelectedMetrics(selectedMetrics.filter((metric) => metricName !== metric))
    } else {
      setSelectedMetrics([...selectedMetrics, metricName])
    }

    if (sendAnalyticsEvents) {
      GA4.sendEvent({
        category: 'Maintenance Trends Page',
        action: 'Maintenance Trend Metric Selection',
        label: `${deviceId}, ${trendName}, ${metricName}, ${isMetricSelected ? 'Unchecked' : 'Checked'}, ${tier}`
      })
    }
  }

  const filteredMetricAndGroupList = useMemo(() => (
    metricAndGroupList.filter((metric: DeviceMetric) => selectedMetrics.includes(metric.name))
  ), [metricAndGroupList, selectedMetrics])

  const maintenanceTrends90secs = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const selected = metricAndGroupList.map((metric) => metric.name)
    setSelectedMetrics(selected)

    if (!maintenanceTrends90secs.current) {
      maintenanceTrends90secs.current = setTimeout(() => Apptentive.sendEvent('maintenance_trends_90sec'), 90000)
    }

    return () => {
      if (maintenanceTrends90secs.current) {
        clearTimeout(maintenanceTrends90secs.current)
      }
    }
  }, [metricAndGroupList])

  return (
    <S.MaintenanceTrend>
      <S.Header><S.Title>{trendName}</S.Title></S.Header>
      <S.Container ref={containerRef}>
        { (!hideChart || showAlert) && (
          <PlotTrend
            {...{ timeZone, trend, deviceId, sendAnalyticsEvents }}
            metricAndGroupList={filteredMetricAndGroupList}
            alerts={alerts}
            tier={tier}
            tooltipId={tooltipId}
          />
        )}

        <AlertsTooltip alerts={alerts} id={tooltipId} />

        <TrendOverviewCard
          {...{
            metricAndGroupList,
            showMetricSelection,
            selectedMetrics,
            handleMetricSelection,
            deviceId,
            trendName,
            hideChart,
            hideRanges,
            sendAnalyticsEvents,
            tier
          }}
        />
      </S.Container>
    </S.MaintenanceTrend>
  )
}

export default MaintenanceTrend

const S = {
  Header: styled.div`
    background: ${color('trendHeaderBackground')};
    padding: 10px 10px 10px 15px;
  `,
  Title: styled.h3`
    text-transform: capitalize;
    margin: 0px;
  `,
  Container: styled.div`
    padding: 10px 10px 10px 15px;
  `,
  MaintenanceTrend: styled.div`
    background: ${color('maintenancePanelBackground')};
  `
}
